import { BFColors } from "@/constants/Colors"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import Toast, {
  BaseToast,
  type ToastConfig,
  type ToastHideParams,
  type ToastProps,
} from "react-native-toast-message"

import ToastErrorIcon from "@/assets/icons/toast-error-alert-icon.svg"
import ToastSuccessIcon from "@/assets/icons/toast-success-check-icon.svg"
import ToastActionIcon from "@/assets/icons/toast-action-required-icon.svg"
import ToastCloseDarkIcon from "@/assets/icons/toast-close-dark.svg"
import ToastCloseLightIcon from "@/assets/icons/toast-close-light.svg"
import { Pressable } from "react-native"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { type ToastShowParams } from "react-native-toast-message/lib/src/types"

type AppBaseToastProps = ToastProps & {
  toastStyle?: "success" | "error" | "action"
}

const AppBaseToast: React.FC<AppBaseToastProps> = ({
  toastStyle = "error",
  ...props
}: AppBaseToastProps) => {
  let ToastIcon = ToastErrorIcon
  let backGroundColor = BFColors.redOrange
  let textColor = BFColors.lightest
  const iconSize: number = 12
  let closeIconColor = BFColors.lightest
  let closeIcon: any = (
    <ToastCloseLightIcon
      width={iconSize}
      height={iconSize}
      color={closeIconColor}
    />
  )

  const { isWeb, screenWidth } = useDeviceAttributes()

  if (toastStyle === "success") {
    ToastIcon = ToastSuccessIcon
    backGroundColor = BFColors.teal
    textColor = BFColors.midnightBlue
    closeIconColor = BFColors.midnightBlue
    closeIcon = (
      <ToastCloseDarkIcon
        width={iconSize}
        height={iconSize}
        color={closeIconColor}
      />
    )
  }

  if (toastStyle === "action") {
    ToastIcon = ToastActionIcon
    backGroundColor = BFColors.calmBlue
    textColor = BFColors.lightest
    closeIconColor = BFColors.lightest
    closeIcon = (
      <ToastCloseLightIcon
        width={iconSize}
        height={iconSize}
        color={closeIconColor}
      />
    )
  }

  return (
    <BaseToast
      {...props}
      style={{
        height: 64,
        width: isWeb ? 350 : screenWidth - 25, // Padding/Margin/Percentages did not work here with this library
        borderRadius: 4,
        borderLeftWidth: 0,
        backgroundColor: backGroundColor,
        justifyContent: "flex-start",
        alignItems: "center",
      }}
      text1Props={{ allowFontScaling: false }}
      text2Props={{ allowFontScaling: false }}
      text1Style={{
        fontSize: 14,
        lineHeight: 18,
        fontFamily: "Figtree-Bold",
        fontWeight: "800",
        color: textColor,
      }}
      text1NumberOfLines={1}
      text2Style={{
        fontSize: 12,
        lineHeight: 16,
        fontFamily: "SpaceGrotesk-Regular",
        fontWeight: "400",
        color: textColor,
      }}
      text2NumberOfLines={2}
      renderLeadingIcon={() => (
        <ToastIcon
          style={{
            marginLeft: 12,
            marginRight: -14,
          }}
          width={24}
          height={24}
        />
      )}
      renderTrailingIcon={() => (
        <Pressable
          testID="toast-close-button"
          onPressIn={() => {
            Toast.hide()
          }}
          style={{
            width: 28,
            height: 28,
            justifyContent: "center",
            alignItems: "center",
            marginRight: 12,
          }}
        >
          {closeIcon}
        </Pressable>
      )}
    />
  )
}

const AppToast: React.FC<ToastProps> = (props: ToastProps) => {
  const insets = useSafeAreaInsets()

  const toastConfig: ToastConfig = {
    error: (props: ToastProps) => (
      <AppBaseToast toastStyle="error" {...props} />
    ),
    success: (props: ToastProps) => (
      <AppBaseToast toastStyle="success" {...props} />
    ),
    action: (props: ToastProps) => (
      <AppBaseToast toastStyle="action" {...props} />
    ),
  }

  return <Toast config={toastConfig} topOffset={insets.top} {...props} />
}

export const hideToast = (params: ToastHideParams) => {
  Toast.hide(params)
}

interface ShowPassiveToastParams {
  title?: string
  message?: string
  visibilityTime?: number
  autoHide?: boolean
  onPress?: () => void
}

interface ShowActionToastParams {
  title?: string
  message?: string
  visibilityTime?: number
  autoHide?: boolean
  onPress?: () => void
}

export const showErrorToast = ({
  title = "Error",
  message,
  visibilityTime = 10000,
  autoHide = true,
  ...rest
}: ShowPassiveToastParams & ToastShowParams) => {
  Toast.show({
    type: "error",
    autoHide,
    text1: title,
    text2: message,
    visibilityTime,
    ...rest,
  })
}

export const showSuccessToast = ({
  title = "Success",
  message,
  visibilityTime = 5000,
  autoHide = true,
  ...rest
}: ShowPassiveToastParams) => {
  Toast.show({
    type: "success",
    autoHide,
    text1: title,
    text2: message,
    visibilityTime,
    ...rest,
  })
}

export const showActionToast = ({
  title = "Action Required",
  message,
  visibilityTime = 4000,
  autoHide = false,
  onPress,
}: ShowActionToastParams) => {
  Toast.show({
    type: "action",
    autoHide,
    text1: title,
    text2: message,
    visibilityTime,
    onPress,
  })
}

export default AppToast
